import {
  faEye,
  faEyeSlash,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Tooltip } from '@/common/Tooltip';
import { formatRelativeTime } from '~/utils/dateFormat';
import { NotificationCheckbox } from './NotificationCheckbox';
import type { NotificationType } from './types';

type NotificationItemProps = {
  notification: NotificationType;
  isSelected: boolean;
  isReadView: boolean;
  onToggleSelect: (id: number) => void;
  onToggleRead: (id: number) => void;
  onDelete: (id: number) => void;
};

export function NotificationItemComponent({
  notification,
  isSelected,
  isReadView,
  onToggleSelect,
  onToggleRead,
  onDelete,
}: NotificationItemProps) {
  const { t, i18n } = useTranslation();
  const actionIcon = isReadView ? faEye : faEyeSlash;
  // Translation Needed
  const actionLabel = isReadView ? t('wbs.mark-unread-2') : 'Mark read';

  return (
    <ItemContainer>
      <NotificationCheckbox
        checked={isSelected}
        onChange={() => onToggleSelect(notification.id)}
      />
      {/* Translation Needed - alt */}
      <AvatarImg src={notification.avatar_url} alt="Avatar" />
      <NotificationContent>
        <NotificationMessage>
          {notification.description.map((part, idx) =>
            part.bold ? (
              <strong key={`${notification.id}-${idx}`}>{part.text}</strong>
            ) : (
              <span key={`${notification.id}-${idx}`}>{part.text}</span>
            ),
          )}
        </NotificationMessage>
        <NotificationTime>
          {formatRelativeTime(notification.created_at, i18n.language)}
        </NotificationTime>
      </NotificationContent>
      <ActionIcons>
        <Tooltip text={actionLabel} position="top">
          <IconButton
            aria-label={actionLabel}
            onClick={() => onToggleRead(notification.id)}
          >
            <FontAwesomeIcon icon={actionIcon} />
          </IconButton>
        </Tooltip>
        <Tooltip text={t('actions.delete')} position="top">
          <IconButton
            aria-label={t('actions.delete')}
            onClick={() => onDelete(notification.id)}
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </IconButton>
        </Tooltip>
      </ActionIcons>
    </ItemContainer>
  );
}

const ItemContainer = styled.div`
  align-items: center;
  background: var(--white);
  border-bottom: 1px solid var(--grey-300);
  cursor: pointer;
  display: flex;
  gap: 20px;
  padding: 15px 16px 15px 20px;
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &:hover {
    background: var(--grey-50);
  }
`;

const AvatarImg = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 52px;
  object-fit: cover;
  width: 52px;
`;

const NotificationContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const NotificationMessage = styled.div`
  color: var(--grey-1000);
  font-family: var(--open-sans);
  font-size: 16px;
  letter-spacing: -0.2px;

  strong {
    font-weight: 600;
  }
`;

const NotificationTime = styled.div`
  color: var(--grey-600);
  font-family: var(--open-sans);
  font-size: 14px;
  font-weight: 300;
`;

const ActionIcons = styled.div`
  display: flex;
`;

const IconButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: var(--grey-500);
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  transition: color 0.2s;
`;
